import { Injectable } from '@angular/core';
import { ApplicationStatus } from '../onboarding/models/onboarding-models';

export interface PortalImpersonationState {
  roleLabel: string;
  status: ApplicationStatus;
  userStatus: string;
  isCorporateAdmin: boolean;
  canTrade: boolean;
  canSign: boolean;
  rateTierId?: number | null;
  rateTierName?: string | null;
}

const STORAGE_KEY = 'ccfx_dev_impersonation_cp';

/**
 * Dev-only service that overrides the active portal user's profile for local testing.
 * Only operates when the app is running on localhost.
 * State is stored in sessionStorage so it clears automatically on tab close.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  /** True on localhost and staging. Feature is disabled on all other hosts. */
  static readonly isLocalhost = ['localhost', 'ws-staging-portal.ccfx-cce.ca'].includes(
    window.location.hostname,
  );

  /**
   * Returns the active impersonation state, or null if none is set
   * or if not running on localhost.
   */
  getState(): PortalImpersonationState | null {
    if (!ImpersonationService.isLocalhost) return null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PortalImpersonationState) : null;
    } catch {
      return null;
    }
  }

  isActive(): boolean {
    return this.getState() !== null;
  }

  /**
   * Activates impersonation with the given profile and reloads the page
   * so the app re-initializes with the overridden state.
   */
  activate(state: PortalImpersonationState): void {
    if (!ImpersonationService.isLocalhost) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.location.reload();
  }

  /**
   * Clears impersonation and reloads the page to restore real state.
   */
  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  }
}
